import React, { useState, useEffect, useMemo, useRef } from "react"
import { Link } from "react-router-dom"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import {
  XIcon,
  ChevronDownIcon,
  SearchIcon,
  AlertTriangleIcon,
  ExternalLinkIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowUpDownIcon,
} from "lucide-react"
import type { SortCol } from "./types"

export function FormRow({
  label, field, form, setForm, type = "text", mono = false,
}: {
  label: string
  field: string
  form: Record<string, string>
  setForm: React.Dispatch<React.SetStateAction<Record<string, string>>>
  type?: string
  mono?: boolean
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-xs">{label}</Label>
      <Input
        type={type}
        value={form[field] ?? ""}
        onChange={(e) => setForm((prev) => ({ ...prev, [field]: e.target.value }))}
        className={cn("h-8 text-sm", mono && "font-mono")}
      />
    </div>
  )
}

export function CityField({
  label, value, matchedId,
}: {
  label: string
  value: string | null | undefined
  matchedId: string | null | undefined
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="text-sm">
        {value ? (
          matchedId ? (
            <Link
              to="/geo/cities"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary hover:underline"
              title="Ver ciudad registrada"
            >
              {value}<ExternalLinkIcon className="size-3 shrink-0 opacity-60" />
            </Link>
          ) : (
            <span className="inline-flex items-center gap-1.5" title="Ciudad no registrada en el sistema">
              {value}<AlertTriangleIcon className="size-3.5 shrink-0 text-amber-500" />
            </span>
          )
        ) : (
          <span className="text-muted-foreground/50">—</span>
        )}
      </dd>
    </div>
  )
}

export function Field({
  label, value, mono = false,
}: {
  label: string
  value: string | null | undefined
  mono?: boolean
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={mono ? "font-mono text-sm" : "text-sm"}>
        {value || <span className="text-muted-foreground/50">—</span>}
      </dd>
    </div>
  )
}

export function SheetSearchSelect({
  value, onChange, items, placeholder = "Buscar...", label,
}: {
  value: string
  onChange: (v: string) => void
  items: { label: string; value: string }[]
  placeholder?: string
  label: string
}) {
  const [query, setQuery] = useState("")
  const [open, setOpen]   = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim()
    if (!q) return items
    const matched = items.filter(i => i.label.toLowerCase().includes(q))
    return matched.sort((a, b) => {
      const aStarts = a.label.toLowerCase().startsWith(q) ? 0 : 1
      const bStarts = b.label.toLowerCase().startsWith(q) ? 0 : 1
      return aStarts - bStarts
    })
  }, [items, query])

  const selectedLabel = items.find(i => i.value === value)?.label ?? ""

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false); setQuery("")
      }
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  return (
    <div className="grid gap-1.5">
      <Label className="text-xs">{label}</Label>
      <div ref={wrapRef} className="relative">
        <div
          className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm cursor-pointer hover:bg-muted/40 transition-colors"
          onClick={() => { setOpen(o => !o); setQuery("") }}
        >
          <span className={selectedLabel ? "text-foreground" : "text-muted-foreground"}>
            {selectedLabel || placeholder}
          </span>
          <div className="flex items-center gap-1">
            {value && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onChange(""); setQuery("") }}
                className="text-muted-foreground hover:text-foreground"
              >
                <XIcon className="size-3.5" />
              </button>
            )}
            <ChevronDownIcon className="size-4 text-muted-foreground" />
          </div>
        </div>
        {open && (
          <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md">
            <div className="p-1.5 border-b">
              <input
                autoFocus
                className="w-full rounded px-2 py-1 text-sm bg-transparent outline-none placeholder:text-muted-foreground"
                placeholder="Buscar..."
                value={query}
                onChange={e => setQuery(e.target.value)}
              />
            </div>
            <div className="max-h-52 overflow-y-auto">
              {filtered.length === 0 ? (
                <p className="px-3 py-4 text-center text-xs text-muted-foreground">Sin resultados</p>
              ) : filtered.map(item => (
                <button
                  key={item.value}
                  type="button"
                  className={cn(
                    "w-full text-left px-3 py-1.5 text-sm hover:bg-muted transition-colors",
                    item.value === value && "bg-muted font-medium"
                  )}
                  onClick={() => { onChange(item.value); setOpen(false); setQuery("") }}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export function FilterCombobox({
  value, onChange, options, placeholder, className,
}: {
  value: string
  onChange: (v: string) => void
  options: string[]
  placeholder: string
  className?: string
}) {
  const [open, setOpen]   = useState(false)
  const [query, setQuery] = useState("")
  const wrapRef  = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const filtered = useMemo(() => {
    if (!query.trim()) return options
    const q = query.toLowerCase()
    return options.filter((o) => o.toLowerCase().includes(q))
  }, [options, query])

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false); setQuery("")
      }
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  function select(v: string) { onChange(v); setOpen(false); setQuery("") }

  function clear(e: React.MouseEvent) {
    e.stopPropagation(); onChange(""); setQuery("")
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  return (
    <div ref={wrapRef} className={cn("relative", className)}>
      {value ? (
        <button
          type="button"
          onClick={clear}
          className="flex h-9 w-full items-center gap-1.5 rounded-md border border-input bg-background px-3 text-sm hover:bg-accent transition-colors"
        >
          <span className="flex-1 text-left truncate">{value}</span>
          <XIcon className="size-3.5 shrink-0 text-muted-foreground" />
        </button>
      ) : (
        <div className="flex h-9 items-center gap-2 rounded-md border border-input bg-background px-2.5 focus-within:ring-1 focus-within:ring-ring">
          <SearchIcon className="size-3.5 shrink-0 text-muted-foreground" />
          <input
            ref={inputRef}
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            placeholder={placeholder}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true) }}
            onFocus={() => setOpen(true)}
          />
          {query && (
            <button
              type="button"
              onClick={() => { setQuery(""); inputRef.current?.focus() }}
              className="text-muted-foreground hover:text-foreground"
            >
              <XIcon className="size-3.5" />
            </button>
          )}
        </div>
      )}
      {open && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 max-h-60 overflow-auto rounded-md border bg-popover text-popover-foreground shadow-md">
          {filtered.length === 0 ? (
            <p className="px-3 py-2 text-sm text-muted-foreground">Sin resultados</p>
          ) : (
            filtered.map((opt) => (
              <button
                key={opt}
                type="button"
                className="flex w-full items-center px-3 py-2 text-sm hover:bg-accent text-left"
                onMouseDown={(e) => { e.preventDefault(); select(opt) }}
              >
                {opt}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Like FilterCombobox but accepts structured options with separate label (displayed)
 * and value (stored). Filters by label, returns value on select.
 */
export function FilterComboboxLabeled({
  value, onChange, options, placeholder, className,
}: {
  value: string
  onChange: (v: string) => void
  options: { label: string; value: string }[]
  placeholder: string
  className?: string
}) {
  const [open, setOpen]   = useState(false)
  const [query, setQuery] = useState("")
  const wrapRef  = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const filtered = useMemo(() => {
    if (!query.trim()) return options
    const q = query.toLowerCase()
    return options.filter((o) => o.label.toLowerCase().includes(q))
  }, [options, query])

  const selectedLabel = options.find((o) => o.value === value)?.label ?? ""

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false); setQuery("")
      }
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  function select(v: string) { onChange(v); setOpen(false); setQuery("") }

  function clear(e: React.MouseEvent) {
    e.stopPropagation(); onChange(""); setQuery("")
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  return (
    <div ref={wrapRef} className={cn("relative", className)}>
      {value ? (
        <button
          type="button"
          onClick={clear}
          className="flex h-9 w-full items-center gap-1.5 rounded-md border border-input bg-background px-3 text-sm hover:bg-accent transition-colors"
        >
          <span className="flex-1 text-left truncate">{selectedLabel}</span>
          <XIcon className="size-3.5 shrink-0 text-muted-foreground" />
        </button>
      ) : (
        <div className="flex h-9 items-center gap-2 rounded-md border border-input bg-background px-2.5 focus-within:ring-1 focus-within:ring-ring">
          <SearchIcon className="size-3.5 shrink-0 text-muted-foreground" />
          <input
            ref={inputRef}
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            placeholder={placeholder}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true) }}
            onFocus={() => setOpen(true)}
          />
          {query && (
            <button
              type="button"
              onClick={() => { setQuery(""); inputRef.current?.focus() }}
              className="text-muted-foreground hover:text-foreground"
            >
              <XIcon className="size-3.5" />
            </button>
          )}
        </div>
      )}
      {open && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 max-h-60 overflow-auto rounded-md border bg-popover text-popover-foreground shadow-md">
          {filtered.length === 0 ? (
            <p className="px-3 py-2 text-sm text-muted-foreground">Sin resultados</p>
          ) : (
            filtered.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className="flex w-full items-center px-3 py-2 text-sm hover:bg-accent text-left"
                onMouseDown={(e) => { e.preventDefault(); select(opt.value) }}
              >
                {opt.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}

export function SortIcon({
  col, sortBy, sortDir,
}: {
  col: SortCol
  sortBy: SortCol | null
  sortDir: "asc" | "desc"
}) {
  if (sortBy !== col)
    return <ArrowUpDownIcon className="size-3 text-muted-foreground/50" />
  return sortDir === "asc"
    ? <ArrowUpIcon className="size-3" />
    : <ArrowDownIcon className="size-3" />
}
